// =============================================================
// ToastHost.jsx (React class) + helper `toast`
// Toaster genérico estilo MDB con opciones de posición, tamaño, colores,
// timeout (auto-cierre), botón/cierre manual y animaciones (fade/slide).
// -------------------------------------------------------------
// USO BÁSICO:
// 1) Monta el host UNA VEZ, idealmente cerca de <App/>:
//    <ToastHost />
// 2) Lanza toasts desde cualquier parte:
//    import { toast } from './ToastHost';
//    toast.success('Guardado correctamente');
//    toast.show({ title: 'Atención', message: 'Revisa los campos', type: 'warning', position: 'bottom-center' });
//
// Opciones de `toast.show()`:
// - title?: string
// - message: string (puede contener HTML simple si usas dangerouslySetInnerHTML)
// - type?: 'success' | 'info' | 'warning' | 'danger' (colores por defecto)
// - bgColor?: string  // override del fondo
// - textColor?: string // override del texto
// - timeout?: number   // ms; 0 o undefined -> no autocierra
// - position?: 'top-left' | 'top-center' | 'top-right' | 'middle-center' | 'bottom-left' | 'bottom-center' | 'bottom-right'
// - size?: 'auto' | 'wide' // auto = se adapta; wide = ocupa casi todo el ancho
// - animation?: 'fade' | 'slide'
// - closeButtonText?: string // si lo pones, aparece botón de cerrar además de la ✕
// - onClose?: (reason: 'timeout' | 'manual') => void
// - pauseOnHover?: boolean (default true)
// - id?: string (opcional si quieres controlar el id desde fuera)
//
// NOTA: Inspirado en MDB toasts, pero sin dependencia directa del componente.
// Puedes ajustar clases en CSS para mimetizarlo 100%.
// =============================================================

import React from "react";
import ReactDOM from "react-dom";

import "./ToastHost.css";

let __toastHostRef = null; // referencia global sencilla

export const toast = {
  show(opts) {
    if (__toastHostRef) return __toastHostRef.addToast(opts);
    // fallback por si el host aún no montó: usa evento global
    const ev = new CustomEvent("app:toast", { detail: opts });
    window.dispatchEvent(ev);
    return null;
  },
  success(message, extra = {}) {
    return this.show({ message, type: "success", ...extra });
  },
  info(message, extra = {}) {
    return this.show({ message, type: "info", ...extra });
  },
  warning(message, extra = {}) {
    return this.show({ message, type: "warning", ...extra });
  },
  danger(message, extra = {}) {
    return this.show({ message, type: "danger", ...extra });
  },
  error(message, extra = {}) {
    return this.show({ message, type: "danger", ...extra });
  },
  close(id) {
    if (__toastHostRef) __toastHostRef.closeToast(id);
  },
};

const DEFAULTS = {
  position: "top-right",
  animation: "fade",
  size: "auto", // 'auto' | 'wide'
  timeout: 5500, // ms
  pauseOnHover: true,
  zIndex: 1060,
  maxToasts: 5,
};

function uid() {
  return `t_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

function positionToContainerKey(pos) {
  // agrupa por posición para apilar correctamente
  return pos || DEFAULTS.position;
}

function computeSlideClass(pos, phase /* 'in' | 'out' */) {
  const edge = pos || DEFAULTS.position;
  const mapIn = {
    "top-left": "t-slide-in-top-left",
    "top-center": "t-slide-in-top",
    "top-right": "t-slide-in-top-right",
    "middle-center": "t-fade-in", // en centro mejor fade por defecto
    "bottom-left": "t-slide-in-bottom-left",
    "bottom-center": "t-slide-in-bottom",
    "bottom-right": "t-slide-in-bottom-right",
  };
  const mapOut = {
    "top-left": "t-slide-out-top-left",
    "top-center": "t-slide-out-top",
    "top-right": "t-slide-out-top-right",
    "middle-center": "t-fade-out",
    "bottom-left": "t-slide-out-bottom-left",
    "bottom-center": "t-slide-out-bottom",
    "bottom-right": "t-slide-out-bottom-right",
  };
  return phase === "in"
    ? mapIn[edge] || "t-slide-in-top-right"
    : mapOut[edge] || "t-slide-out-top-right";
}

class ToastItem extends React.Component {
  // Props: toast (obj), onClose(id, reason)
  state = { phase: "in" }; // 'in' | 'out'

  componentDidMount() {
    const { toast } = this.props;
    if (toast.timeout && toast.timeout > 0) {
      this.armTimer(toast.timeout);
    }
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  armTimer(ms) {
    this.clearTimers();
    this.timerId = setTimeout(() => this.requestClose("timeout"), ms);
  }

  clearTimers() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (this.animTimer) {
      clearTimeout(this.animTimer);
      this.animTimer = null;
    }
  }

  requestClose = (reason) => {
    // ejecuta animación de salida y notifica al terminar
    this.setState({ phase: "out" });
    const duration = 300; // debe coincidir con CSS
    this.animTimer = setTimeout(
      () => this.props.onClose?.(this.props.toast.id, reason),
      duration
    );
  };

  onMouseEnter = () => {
    const { toast } = this.props;
    if (toast.pauseOnHover && this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  };

  onMouseLeave = () => {
    const { toast } = this.props;
    if (toast.pauseOnHover && toast.timeout && !this.timerId) {
      this.armTimer(800); // le damos un pequeño margen al salir
    }
  };

  render() {
    const { toast } = this.props;
    const { phase } = this.state;

    const variant = toast.type || "info";
    const sizeClass = toast.size === "wide" ? "t-wide" : "t-auto";
    const baseAnim =
      toast.animation === "slide"
        ? computeSlideClass(toast.position, phase)
        : phase === "in"
        ? "t-fade-in"
        : "t-fade-out";

    const style = {
      background: toast.bgColor || undefined,
      color: toast.textColor || undefined,
    };

    return (
      <div
        className={`t-toast ${sizeClass} t-${variant} ${baseAnim}`}
        role="status"
        aria-live={variant === "danger" ? "assertive" : "polite"}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        style={style}
      >
        <div className="t-head">
          {toast.title ? (
            <strong className="t-title">{toast.title}</strong>
          ) : null}
          <button
            className="t-close"
            aria-label="Cerrar"
            onClick={() => this.requestClose("manual")}
          >
            ×
          </button>
        </div>
        <div className="t-body">
          {toast.dangerouslySetInnerHTML ? (
            <div dangerouslySetInnerHTML={{ __html: toast.message }} />
          ) : (
            <div>{toast.message}</div>
          )}
        </div>
        {toast.closeButtonText ? (
          <div className="t-actions">
            <button
              className="t-btn"
              onClick={() => this.requestClose("manual")}
            >
              {toast.closeButtonText}
            </button>
          </div>
        ) : null}
      </div>
    );
  }
}

export class ToastHost extends React.Component {
  static defaultProps = {
    defaultPosition: DEFAULTS.position,
    defaultAnimation: DEFAULTS.animation,
    defaultTimeout: DEFAULTS.timeout,
    defaultSize: DEFAULTS.size,
    zIndex: DEFAULTS.zIndex,
    containerId: "app-toast-host",
    // Si prefieres Portal al body (recomendado), deja usePortal=true
    usePortal: true,
  };

  constructor(props) {
    super(props);
    this.state = { toasts: [] }; // cada toast: {...opts, id, position}

    this.boundOnGlobalToast = this.onGlobalToast.bind(this);
    this.boundOnResize = this.onResize.bind(this);
  }

  componentDidMount() {
    __toastHostRef = this;
    window.addEventListener("app:toast", this.boundOnGlobalToast);
    window.addEventListener("resize", this.boundOnResize);
  }

  componentWillUnmount() {
    if (__toastHostRef === this) __toastHostRef = null;
    window.removeEventListener("app:toast", this.boundOnGlobalToast);
    window.removeEventListener("resize", this.boundOnResize);
  }

  onResize() {
    // reservado para futuros cálculos (p.ej., posiciones responsivas)
    this.forceUpdate();
  }

  onGlobalToast(ev) {
    const opts = ev.detail || {};
    this.addToast(opts);
  }

  addToast = (opts) => {
    const id = opts.id || uid();
    const toastObj = {
      id,
      title: opts.title,
      message: opts.message,
      dangerouslySetInnerHTML: !!opts.dangerouslySetInnerHTML,
      type: opts.type || "info",
      bgColor: opts.bgColor,
      textColor: opts.textColor,
      timeout:
        typeof opts.timeout === "number"
          ? opts.timeout
          : this.props.defaultTimeout,
      position: opts.position || this.props.defaultPosition,
      size: opts.size || this.props.defaultSize,
      animation: opts.animation || this.props.defaultAnimation,
      closeButtonText: opts.closeButtonText,
      onClose: typeof opts.onClose === "function" ? opts.onClose : null,
      pauseOnHover:
        opts.pauseOnHover !== undefined ? !!opts.pauseOnHover : true,
    };

    this.setState((s) => {
      const maxToasts =
        typeof this.props.maxToasts === "number"
          ? this.props.maxToasts
          : DEFAULTS.maxToasts;

      const nextToasts = [...s.toasts, toastObj];
      if (maxToasts > 0 && nextToasts.length > maxToasts) {
        nextToasts.splice(0, nextToasts.length - maxToasts);
      }

      return { toasts: nextToasts };
    });
    return id;
  };

  closeToast = (id, reason = "manual") => {
    // La animación de salida se gestiona en ToastItem; aquí sólo retiramos cuando acabe
    this.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    const t = this.state.toasts.find((x) => x.id === id);
    t?.onClose && t.onClose(reason);
  };

  // agrupamos por posición para contenedores fijos
  groupByPosition(toasts) {
    const groups = {};
    toasts.forEach((t) => {
      const key = positionToContainerKey(t.position);
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }

  renderContainers() {
    const groups = this.groupByPosition(this.state.toasts);
    const positions = [
      "top-left",
      "top-center",
      "top-right",
      "middle-center",
      "bottom-left",
      "bottom-center",
      "bottom-right",
    ];

    return positions.map((pos) => {
      const list = groups[pos] || [];
      if (!list.length) return null;
      return (
        <div
          key={pos}
          className={`t-container t-${pos}`}
          style={{ zIndex: this.props.zIndex }}
        >
          {list.map((t) => (
            <ToastItem
              key={t.id}
              toast={t}
              onClose={(id, reason) => this.closeToast(id, reason)}
            />
          ))}
        </div>
      );
    });
  }

  renderHost() {
    return (
      <div id={this.props.containerId} className="t-host-root">
        {this.renderContainers()}
      </div>
    );
  }

  render() {
    if (!this.props.usePortal) return this.renderHost();
    return ReactDOM.createPortal(this.renderHost(), document.body);
  }
}
